import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import logger from '../utils/logger';

const TeamManagement = () => {
  const { isAdmin, refreshTeam } = useAuth();
  const [teams, setTeams] = useState([]);
  const [domains, setDomains] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [memberOpen, setMemberOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamDomain, setTeamDomain] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [teamsResponse, usersResponse] = await Promise.all([
        api.getTeams(),
        api.getTeamAvailableUsers()
      ]);
      if (teamsResponse.success) {
        setTeams(teamsResponse.teams || []);
      }
      if (usersResponse.success) {
        setDomains(usersResponse.domains || []);
        setUsers(usersResponse.users || []);
      }
    } catch (error) {
      logger.error('Error fetching teams:', error);
      showSnackbar(error.response?.data?.message || 'Failed to load teams', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const candidateMembers = useMemo(() => {
    if (!selectedTeam) return [];
    return users.filter(
      (user) => user.domain === selectedTeam.company_domain && !user.team_id
    );
  }, [users, selectedTeam]);

  const handleCreateTeam = async () => {
    if (!teamName.trim() || !teamDomain) {
      showSnackbar('Name and company domain are required', 'warning');
      return;
    }
    try {
      const response = await api.createTeam({ name: teamName.trim(), company_domain: teamDomain });
      if (response.success) {
        showSnackbar('Team created', 'success');
        setCreateOpen(false);
        setTeamName('');
        setTeamDomain('');
        fetchData();
        refreshTeam?.();
      } else {
        showSnackbar(response.message || 'Failed to create team', 'error');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to create team', 'error');
    }
  };

  const handleAddMember = async () => {
    if (!selectedTeam || !selectedUserId) {
      showSnackbar('Select a user', 'warning');
      return;
    }
    try {
      const response = await api.addTeamMember(selectedTeam.id, selectedUserId);
      if (response.success) {
        showSnackbar('Member added', 'success');
        setMemberOpen(false);
        setSelectedUserId('');
        fetchData();
        refreshTeam?.();
      } else {
        showSnackbar(response.message || 'Failed to add member', 'error');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to add member', 'error');
    }
  };

  const handleRemoveMember = async (team, userId) => {
    try {
      const response = await api.removeTeamMember(team.id, userId);
      if (response.success) {
        showSnackbar('Member removed', 'success');
        fetchData();
        refreshTeam?.();
      } else {
        showSnackbar(response.message || 'Failed to remove member', 'error');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to remove member', 'error');
    }
  };

  const handleDeleteTeam = async (team) => {
    try {
      const response = await api.deleteTeam(team.id);
      if (response.success) {
        showSnackbar('Team removed', 'success');
        fetchData();
        refreshTeam?.();
      } else {
        showSnackbar(response.message || 'Failed to remove team', 'error');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to remove team', 'error');
    }
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <GroupIcon sx={{ mr: 1 }} />
          Teams
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New team
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Members of a team see each other's outreach. Everyone on a team must share that team's email domain, and a user can be on one team.
      </Typography>

      {teams.length === 0 ? (
        <Alert severity="info">No teams yet.</Alert>
      ) : (
        <Stack spacing={2}>
          {teams.map((team) => (
            <Paper key={team.id} variant="outlined">
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle1">{team.name}</Typography>
                  <Chip size="small" label={team.company_domain} sx={{ mt: 0.5 }} />
                </Box>
                <Box>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setSelectedTeam(team);
                      setSelectedUserId('');
                      setMemberOpen(true);
                    }}
                  >
                    Add member
                  </Button>
                  <Button size="small" color="error" onClick={() => handleDeleteTeam(team)}>
                    Remove team
                  </Button>
                </Box>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(team.members || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>No members yet.</TableCell>
                      </TableRow>
                    ) : (
                      team.members.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>{member.display_name || member.username}</TableCell>
                          <TableCell>{member.username}</TableCell>
                          <TableCell>{member.email}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              aria-label="Remove member"
                              onClick={() => handleRemoveMember(team, member.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New team</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Team name"
            fullWidth
            value={teamName}
            onChange={(event) => setTeamName(event.target.value)}
          />
          <TextField
            select
            margin="dense"
            label="Company domain"
            fullWidth
            value={teamDomain}
            onChange={(event) => setTeamDomain(event.target.value)}
          >
            {domains.map((domain) => (
              <MenuItem key={domain} value={domain}>{domain}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTeam}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={memberOpen} onClose={() => setMemberOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add member{selectedTeam ? ` to ${selectedTeam.name}` : ''}</DialogTitle>
        <DialogContent>
          {candidateMembers.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No available users on {selectedTeam?.company_domain}.
            </Alert>
          ) : (
            <TextField
              select
              margin="dense"
              label="User"
              fullWidth
              value={selectedUserId}
              onChange={(event) => setSelectedUserId(event.target.value)}
            >
              {candidateMembers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.display_name || user.username} ({user.email})
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddMember} disabled={!selectedUserId}>Add</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamManagement;
